import * as React from 'react';

interface Props { 
  value: number;
  max?: number;
  color?: string;
  display_progress?: "" | "percent" | "counts" | "both";
  message?: string;
  horizontal?: boolean;
}

const containerStyles: React.CSSProperties = {
  width: '100%',
  backgroundColor: "#e0e0de",
  borderRadius: 50,
  position: 'relative',
  // Flex is still important for minHeight and allowing vertical growth
  display: 'flex',
  alignItems: 'center', 
  justifyContent: 'center',
  minHeight: 20, 
  padding: '5px 0', 
  overflow: 'hidden',
}

const labelStyles: React.CSSProperties = {
  position: 'absolute', 
  // Set left: 0 and right: 0 to force the span to be 100% width of the container
  top: '50%',
  left: 0, 
  right: 0,
  // Only center vertically since we're using left/right 0
  transform: 'translateY(-50%)', 
  
  // Text styling
  padding: '0 8px', // Add horizontal padding for margin
  color: 'black',
  fontWeight: 'bold',
  textAlign: 'center', // Center the text within the 100% width span
  zIndex: 2, 
}

// Styling for the "ghost" span to force container height
const ghostLabelStyles: React.CSSProperties = {
  // Use a relative/normal position element to force the parent div to grow
  position: 'relative', 
  visibility: 'hidden', // Make it invisible
  // Apply necessary text properties to ensure correct height calculation
  fontWeight: 'bold',
  textAlign: 'center',
  // Must be full width to respect text wrapping based on container width
  width: '100%', 
  // Add padding to match the absolute label's horizontal padding
  padding: '0 8px', 
  whiteSpace: 'normal', // Ensure text wraps
}

export default function ProgressBar({ 
  value, 
  max = 100,
  color = "#4CAF50",
  display_progress = "percent",
  message = "",
  horizontal = false
}: Props) {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    setProgress(Math.min(Math.floor(value / max * 100), 100));
  }, [value, max]);

  const progressLabel = React.useMemo(() => {
    // Determine the progress text part
    let progressText = '';
    if (display_progress === "percent") {
      if (progress > 0) {
        progressText = `${progress}%`;
      }
    } else if (display_progress === "counts") {
      progressText = `${value} / ${max}`;
    } else if (display_progress === "both") {
      progressText = `${value} / ${max} (${progress}%)`;
    }
  
    // Combine message and progress text
    if (!message) return progressText;
    if (!progressText) return message;
    return horizontal ? `${message} ${progressText}` : `${message}\n${progressText}`;
  }, [display_progress, progress, value, max, message, horizontal]);
  
  const barWidth = `${progress}%`;

  // The label is split into lines using '\n' in the progressLabel logic, 
  // so we need to render it using map to respect line breaks.
  const labelLines = progressLabel.split('\n');

  return (
    <div style={containerStyles}>
      
      {/* Progress Bar Div - Stays as the base layer */}
      <div style={{
        height: '100%',
        width: barWidth,
        backgroundColor: color,
        borderRadius: 'inherit',
        position: 'absolute', 
        top: 0,
        left: 0,
        zIndex: 1,
      }} />

      {/* Ghost Label and Actual Label */}
      {progressLabel && (
        <>
          {/* 1. GHOST LABEL: Forces the container to expand to the required height */}
          <span style={ghostLabelStyles}>
            {/* Render with line breaks to ensure correct height calculation */}
            {labelLines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < labelLines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </span>

          {/* 2. ACTUAL LABEL: The visible, absolutely-positioned overlay */}
          <span style={labelStyles}>
            {/* Render with line breaks */}
            {labelLines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < labelLines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </span>
        </>
      )}
    </div>
  );
}